import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import type { ChatSearchHit } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useDebounced } from '@/lib/useDebounced';
import { today } from '@/lib/format';
import { DatePickerButton } from '@/components/ui';
import { matchCounter, stepMatch } from './chatSearch';

/**
 * t291u: the compact bar under the conversation header for searching this conversation only (thread replies and file
 * names included; the server checks you can read it). Matches are numbered newest first: Enter or ↑ goes to the next
 * (older) one, Shift+Enter or ↓ to the previous (newer) one, Esc closes. The calendar button is Jump to date.
 */
export function ChatSearchBar({ conversationId, onJump, onTerms, onPickDate, onClose, openDate = false }: {
  conversationId: string;
  onJump: (hit: ChatSearchHit) => void;
  /** The text being searched for (for highlighting), or '' when there's nothing to highlight. */
  onTerms: (q: string) => void;
  onPickDate: (iso: string) => void;
  onClose: () => void;
  /** Opened from "Jump to date": show the calendar straight away. */
  openDate?: boolean;
}) {
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 250);
  const input = useRef<HTMLInputElement>(null);
  const [index, setIndex] = useState(-1);
  const res = useQuery({ queryKey: ['chat-search', conversationId, q], queryFn: () => api.searchConversation(conversationId, q), enabled: q.length > 0, staleTime: 15_000 });
  // Newest first, so "1 of 12" is the most recent match.
  const hits = q && res.data ? [...res.data.hits].reverse() : [];
  const total = hits.length;
  useEffect(() => { if (!openDate) input.current?.focus(); }, [openDate]);
  useEffect(() => { onTerms(res.data && q ? q : ''); }, [q, res.data, onTerms]);
  // A new result set jumps to the newest match.
  useEffect(() => {
    if (!res.data || !q) { setIndex(-1); return; }
    setIndex(res.data.hits.length ? 0 : -1);
    const newest = res.data.hits.at(-1);
    if (newest) onJump(newest);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the results change
  }, [res.data, q]);
  const go = (dir: 1 | -1) => {
    const next = stepMatch(index, total, dir);
    if (next < 0) return;
    setIndex(next);
    onJump(hits[next]);
  };
  const close = () => { onTerms(''); onClose(); };
  return (
    <div className="flex h-11 shrink-0 items-center gap-1 border-b border-slate-200 bg-white px-2 sm:px-4 dark:border-slate-800 dark:bg-slate-950" data-testid="chat-search-bar" role="search">
      <Search className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      <input ref={input} value={text} onChange={e => setText(e.target.value)} placeholder="Search" aria-label="Search this conversation" enterKeyHint="search"
        data-testid="chat-search-input" maxLength={200}
        className="min-w-0 flex-1 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-slate-500"
        onKeyDown={e => {
          if (e.key === 'Escape') { e.preventDefault(); close(); }
          else if (e.key === 'Enter') { e.preventDefault(); go(e.shiftKey ? -1 : 1); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); go(1); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); go(-1); }
        }} />
      {q && <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-slate-600 dark:text-slate-300" data-testid="chat-search-counter" aria-live="polite">
        {matchCounter(index, total, { loading: res.isFetching && !res.data, truncated: res.data?.truncated })}</span>}
      <button type="button" className="rounded p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800" onClick={() => go(1)} disabled={total < 1}
        title="Older match (Enter)" aria-label="Older match" data-testid="chat-search-older"><ChevronUp className="h-4 w-4" /></button>
      <button type="button" className="rounded p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800" onClick={() => go(-1)} disabled={total < 1}
        title="Newer match (Shift+Enter)" aria-label="Newer match" data-testid="chat-search-newer"><ChevronDown className="h-4 w-4" /></button>
      <DatePickerButton value={today()} onPick={onPickDate} defaultOpen={openDate} title="Jump to date" aria-label="Jump to date"
        className="flex items-center gap-1 rounded p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"><CalendarDays className="h-4 w-4" /></DatePickerButton>
      <button type="button" className="rounded p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800" onClick={close} title="Close search (Esc)" aria-label="Close search" data-testid="chat-search-close"><X className="h-4 w-4" /></button>
    </div>
  );
}
